<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* global globalThis */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import {
  predictionFullText,
  predictionPlaceId,
  predictionPrimaryText,
  predictionSecondaryText,
  useAddressAutocomplete,
} from 'dashboard/composables/useAddressAutocomplete';

const props = defineProps({
  modelValue: { type: Object, default: null },
  query: { type: String, default: '' },
  recentSearches: { type: Array, default: () => [] },
  contactId: { type: [Number, String], default: null },
});

const emit = defineEmits([
  'update:query',
  'select',
  'selectRecent',
  'clear',
  'filterRemoved',
  'searchExpanded',
]);

const internalQuery = ref(props.query || '');
const isPanelOpen = ref(false);
const isNormalizing = ref(false);
const geolocationState = ref('idle');
let blurTimer = null;

const {
  sessionToken,
  predictions,
  isAutocompleteLoading,
  autocompleteError,
  ensureSessionToken,
  resetSessionToken,
  clearPredictions,
  debouncedLoadPredictions,
} = useAddressAutocomplete();

const selectedLabel = computed(() => props.modelValue?.label || '');
const hasSelectedLocation = computed(() => Boolean(selectedLabel.value));
const canUseGeolocation = computed(
  () =>
    typeof navigator !== 'undefined' &&
    typeof navigator.geolocation?.getCurrentPosition === 'function'
);
const recentLocationSearches = computed(() =>
  props.contactId ? props.recentSearches.slice(0, 3) : []
);

const slugFor = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const locationPartsFromPrediction = prediction => {
  const fullText = predictionFullText(prediction);
  const secondary = predictionSecondaryText(prediction);
  const parts = String(secondary || fullText)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const cityState = parts[parts.length - 1] || '';
  const [city, state] = cityState.split(/\s+-\s+|\s+/).filter(Boolean);

  return {
    bairro: parts.length > 1 ? parts[0] : '',
    cidade: city || '',
    uf: state || '',
    fullText,
  };
};

const areaFromAddress = (data = {}, fallback = {}) => {
  const bairro = data.neighborhood || data.bairro || fallback.bairro || '';
  const cidade = data.city || data.cidade || fallback.cidade || '';
  const uf = data.state || data.uf || fallback.uf || '';
  const fullAddress =
    data.fullAddress ||
    data.full_address ||
    data.address ||
    fallback.fullText ||
    internalQuery.value;
  const label =
    [bairro, cidade, uf].filter(Boolean).join(', ') ||
    fullAddress ||
    'Localização selecionada';

  return {
    label,
    slug: slugFor([bairro, cidade, uf, fullAddress].filter(Boolean).join(' ')),
    cidade,
    bairro,
    uf,
    lat: data.lat ?? data.latitude ?? fallback.lat ?? null,
    lng: data.lng ?? data.longitude ?? fallback.lng ?? null,
    google_place_id:
      data.googlePlaceId ||
      data.google_place_id ||
      fallback.google_place_id ||
      '',
    full_address: fullAddress,
  };
};

const areaFromNearbyItem = (item = {}, coords = {}) => {
  const attrs = item.additionalAttributes || item.additional_attributes || {};
  const bairro = item.bairro || item.neighborhood || attrs.bairro || '';
  const cidade = item.cidade || item.city || attrs.cidade || '';
  const uf = item.uf || item.state || attrs.uf || '';
  const label =
    [bairro, cidade, uf].filter(Boolean).join(', ') || 'Minha localização';

  return {
    label,
    slug: slugFor(label),
    cidade,
    bairro,
    uf,
    lat: coords.lat,
    lng: coords.lng,
    google_place_id: '',
    full_address: label,
  };
};

const closePanel = () => {
  isPanelOpen.value = false;
};

const openPanel = () => {
  if (!isPanelOpen.value) emit('searchExpanded');
  isPanelOpen.value = true;
  ensureSessionToken();
};

const commitArea = area => {
  internalQuery.value = area.label;
  emit('update:query', area.label);
  emit('select', area);
  clearPredictions();
  closePanel();
};

const handleInput = () => {
  emit('update:query', internalQuery.value);
  openPanel();
  debouncedLoadPredictions(internalQuery.value);
};

const handleFocus = () => {
  if (blurTimer) {
    clearTimeout(blurTimer);
    blurTimer = null;
  }
  openPanel();
  if (internalQuery.value.trim().length >= 3) {
    debouncedLoadPredictions(internalQuery.value);
  }
};

const handleBlur = () => {
  blurTimer = globalThis.setTimeout(closePanel, 150);
};

const selectPrediction = async prediction => {
  const placeId = predictionPlaceId(prediction);
  const fallback = {
    ...locationPartsFromPrediction(prediction),
    google_place_id: placeId,
  };
  const query = predictionFullText(prediction);
  internalQuery.value = query;
  isNormalizing.value = true;

  try {
    const { data } = await ImovelAPI.normalizeAddress({
      query,
      google_place_id: placeId,
      session_token: sessionToken.value,
    });
    commitArea(areaFromAddress(data, fallback));
  } catch {
    commitArea(areaFromAddress({}, fallback));
  } finally {
    resetSessionToken();
    isNormalizing.value = false;
  }
};

const commitManualLocation = () => {
  const query = internalQuery.value.trim();
  if (!query) return;
  commitArea({
    label: query,
    slug: slugFor(query),
    cidade: '',
    bairro: '',
    uf: '',
    lat: null,
    lng: null,
    google_place_id: '',
    full_address: query,
  });
};

const clearLocation = () => {
  internalQuery.value = '';
  emit('update:query', '');
  emit('filterRemoved', props.modelValue);
  emit('clear');
  clearPredictions();
  resetSessionToken();
  closePanel();
};

const selectRecentSearch = search => {
  emit('selectRecent', search);
  closePanel();
};

const useCurrentLocation = () => {
  if (!canUseGeolocation.value || geolocationState.value === 'requesting')
    return;

  geolocationState.value = 'requesting';
  navigator.geolocation.getCurrentPosition(
    async position => {
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      try {
        const { data } = await ImovelAPI.nearby({
          lat: coords.lat,
          lng: coords.lng,
          radius_km: 3,
          limit: 1,
        });
        const firstItem = (data?.payload || data?.data || [])[0] || {};
        commitArea(areaFromNearbyItem(firstItem, coords));
      } catch {
        commitArea(areaFromNearbyItem({}, coords));
      } finally {
        geolocationState.value = 'idle';
      }
    },
    () => {
      geolocationState.value = 'idle';
    },
    { timeout: 6000, maximumAge: 60000 }
  );
};

watch(
  () => props.query,
  value => {
    if (value !== internalQuery.value) internalQuery.value = value || '';
  }
);

onBeforeUnmount(() => {
  if (blurTimer) clearTimeout(blurTimer);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="relative grid gap-2">
    <label
      class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
      for="lais-cockpit-location"
    >
      Localização
    </label>

    <div class="flex min-w-0 gap-2">
      <div class="relative min-w-0 flex-1">
        <span
          class="i-lucide-map-pin absolute left-3 top-1/2 size-4 -translate-y-1/2 text-lais-text-muted"
          aria-hidden="true"
        />
        <input
          id="lais-cockpit-location"
          v-model="internalQuery"
          type="search"
          placeholder="Rua, bairro ou cidade"
          class="h-10 w-full rounded-lais border border-lais-border bg-white py-2 pl-9 pr-3 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown.enter.prevent="commitManualLocation"
        />
      </div>
      <button
        v-if="canUseGeolocation"
        type="button"
        class="grid h-10 w-10 shrink-0 place-items-center rounded-lais border border-lais-border bg-white text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-wait disabled:opacity-60"
        :disabled="geolocationState === 'requesting'"
        title="Usar minha localização"
        @mousedown.prevent
        @click="useCurrentLocation"
      >
        <span
          class="size-4"
          :class="
            geolocationState === 'requesting'
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-locate-fixed'
          "
          aria-hidden="true"
        />
      </button>
    </div>

    <div v-if="hasSelectedLocation" class="flex min-w-0 flex-wrap gap-2">
      <span
        class="inline-flex max-w-full items-center gap-1.5 rounded-full bg-lais-primary-50 px-2.5 py-1 text-[12px] font-semibold text-lais-primary"
      >
        <span class="i-lucide-map-pin size-3.5 shrink-0" aria-hidden="true" />
        <span class="min-w-0 truncate">{{ selectedLabel }}</span>
        <button
          type="button"
          class="grid size-5 shrink-0 place-items-center rounded-full hover:bg-white"
          aria-label="Remover localização"
          @mousedown.prevent
          @click="clearLocation"
        >
          <span class="i-lucide-x size-3.5" aria-hidden="true" />
        </button>
      </span>
    </div>

    <div
      v-if="isPanelOpen"
      class="fixed inset-x-0 bottom-0 z-40 grid max-h-[70vh] gap-2 overflow-y-auto rounded-t-lais border border-lais-border bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-xl md:absolute md:bottom-auto md:top-full md:mt-2 md:max-h-[360px] md:rounded-lais"
      @mousedown.prevent
    >
      <button
        v-for="prediction in predictions"
        :key="predictionPlaceId(prediction) || predictionFullText(prediction)"
        type="button"
        class="flex min-w-0 items-start gap-3 rounded-lais border border-lais-border bg-white p-3 text-left transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
        @click="selectPrediction(prediction)"
      >
        <span
          class="i-lucide-map-pin mt-0.5 size-4 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
        <span class="min-w-0">
          <span class="block truncate text-[13px] font-semibold text-lais-text">
            {{ predictionPrimaryText(prediction) }}
          </span>
          <span class="block truncate text-[12px] text-lais-text-muted">
            {{
              predictionSecondaryText(prediction) ||
              predictionFullText(prediction)
            }}
          </span>
        </span>
      </button>

      <div
        v-if="(isAutocompleteLoading || isNormalizing) && !predictions.length"
        class="rounded-lais border border-lais-border p-3 text-center text-[13px] text-lais-text-muted"
      >
        Buscando localizações...
      </div>

      <div
        v-else-if="
          internalQuery.trim().length >= 3 &&
          !predictions.length &&
          !autocompleteError
        "
        class="rounded-lais border border-lais-border p-3 text-center text-[13px] text-lais-text-muted"
      >
        Nenhuma localização encontrada.
      </div>

      <p
        v-if="autocompleteError"
        class="rounded-lais border border-rose-100 bg-rose-50 p-3 text-[13px] text-rose-700"
      >
        {{ autocompleteError }}
      </p>

      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          class="inline-flex h-8 items-center gap-1.5 rounded-lais border border-lais-border bg-white px-2.5 text-[12px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
          @click="commitManualLocation"
        >
          <span class="i-lucide-pen-line size-3.5" aria-hidden="true" />
          Digitar manualmente
        </button>
        <button
          v-if="canUseGeolocation"
          type="button"
          class="inline-flex h-8 items-center gap-1.5 rounded-lais border border-lais-border bg-white px-2.5 text-[12px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-wait disabled:opacity-60"
          :disabled="geolocationState === 'requesting'"
          @click="useCurrentLocation"
        >
          <span
            class="size-3.5"
            :class="
              geolocationState === 'requesting'
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-locate-fixed'
            "
            aria-hidden="true"
          />
          Usar minha localização
        </button>
      </div>

      <div v-if="recentLocationSearches.length" class="grid gap-2 pt-1">
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          Buscas recentes
        </p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="search in recentLocationSearches"
            :key="search.id || search.slug"
            type="button"
            class="max-w-full rounded-full bg-lais-bg-soft px-2.5 py-1 text-[12px] font-semibold text-lais-text hover:bg-lais-primary-50 hover:text-lais-primary"
            @click="selectRecentSearch(search)"
          >
            <span class="block max-w-[180px] truncate">
              {{ search.label || search.slug }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
